import { useState } from "react";
import type { ColumnToggle } from "../../Table";

export interface VisibilityColumn {
  readonly id: string;
  readonly label: string;
  readonly hiddenByDefault?: boolean;
  readonly canHide?: boolean;
}

export interface ColumnVisibility {
  readonly hidden: ReadonlySet<string>;
  readonly isVisible: (id: string) => boolean;
  readonly setVisible: (id: string, visible: boolean) => void;
  readonly toggles: readonly ColumnToggle[];
  /** Changes on every reset; a DataTable given it as `widthsReset` drops its resized widths. */
  readonly widthsReset: number;
  readonly reset: () => void;
}

export function useColumnVisibility(columns: readonly VisibilityColumn[]): ColumnVisibility {
  const defaults = () => new Set(columns.filter((c) => c.hiddenByDefault).map((c) => c.id));
  const [hidden, setHidden] = useState<ReadonlySet<string>>(defaults);
  const [widthsReset, setWidthsReset] = useState(0);
  const setVisible = (id: string, visible: boolean) => {
    setHidden((current) => {
      const next = new Set(current);
      if (visible) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  return {
    hidden,
    isVisible: (id) => !hidden.has(id),
    setVisible,
    toggles: columns.map((c) => ({
      id: c.id,
      label: c.label,
      visible: !hidden.has(c.id),
      canHide: c.canHide ?? true,
      onToggle: (visible: boolean) => {
        setVisible(c.id, visible);
      },
    })),
    widthsReset,
    reset: () => {
      setHidden(defaults());
      setWidthsReset((n) => n + 1);
    },
  };
}
